class CompleteTheLookCarousel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.viewport = this.querySelector('[data-complete-look-viewport]');
    this.cards = Array.from(this.querySelectorAll('[data-complete-look-card]'));
    this.nextButton = this.querySelector('[data-complete-look-next]');
    this.previousButton = this.querySelector('[data-complete-look-previous]');

    if (!this.viewport || this.cards.length === 0) return;

    this.dataset.initialized = 'true';
    this.currentIndex = 0;

    this.nextButton?.addEventListener('click', () => this.move(1));
    this.previousButton?.addEventListener('click', () => this.move(-1));
    this.viewport.addEventListener('scroll', () => this.queueUpdate(), { passive: true });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.viewport);
    } else {
      window.addEventListener('resize', this.handleResize = () => this.update(), { passive: true });
    }

    requestAnimationFrame(() => this.update());
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    if (this.handleResize) window.removeEventListener('resize', this.handleResize);
    if (this.updateFrame) cancelAnimationFrame(this.updateFrame);
  }

  queueUpdate() {
    if (this.updateFrame) return;
    this.updateFrame = requestAnimationFrame(() => {
      this.updateFrame = null;
      this.update();
    });
  }

  move(direction) {
    const viewportRect = this.viewport.getBoundingClientRect();
    const tolerance = 2;
    const fullyVisibleIndexes = this.cards
      .map((card, index) => ({ index, rect: card.getBoundingClientRect() }))
      .filter(({ rect }) => rect.left >= viewportRect.left - tolerance && rect.right <= viewportRect.right + tolerance)
      .map(({ index }) => index);

    if (fullyVisibleIndexes.length === 0) return;

    const firstVisible = Math.min(...fullyVisibleIndexes);
    const lastVisible = Math.max(...fullyVisibleIndexes);
    const targetIndex = direction > 0
      ? Math.min(lastVisible + 1, this.cards.length - 1)
      : Math.max(firstVisible - 1, 0);

    const targetRect = this.cards[targetIndex].getBoundingClientRect();
    const isRtl = getComputedStyle(this.viewport).direction === 'rtl';
    const visualDelta = direction > 0
      ? targetRect.left - viewportRect.left
      : targetRect.right - viewportRect.right;
    const left = isRtl ? -visualDelta : visualDelta;

    this.viewport.scrollBy({
      left,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  }

  update() {
    const viewportRect = this.viewport.getBoundingClientRect();
    const isRtl = getComputedStyle(this.viewport).direction === 'rtl';
    const startEdge = isRtl ? viewportRect.right : viewportRect.left;

    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    this.cards.forEach((card, index) => {
      const rect = card.getBoundingClientRect();
      const cardStart = isRtl ? rect.right : rect.left;
      const distance = Math.abs(cardStart - startEdge);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    this.currentIndex = closestIndex;
    this.updateButtons();
  }

  updateButtons() {
    const hasOverflow = this.viewport.scrollWidth > this.viewport.clientWidth + 2;
    const viewportRect = this.viewport.getBoundingClientRect();
    const tolerance = 2;
    const firstRect = this.cards[0].getBoundingClientRect();
    const lastRect = this.cards[this.cards.length - 1].getBoundingClientRect();
    const firstVisible = firstRect.left >= viewportRect.left - tolerance && firstRect.right <= viewportRect.right + tolerance;
    const lastVisible = lastRect.left >= viewportRect.left - tolerance && lastRect.right <= viewportRect.right + tolerance;

    if (this.nextButton) {
      this.nextButton.hidden = !hasOverflow;
      this.nextButton.disabled = lastVisible;
    }

    if (this.previousButton) {
      this.previousButton.hidden = !hasOverflow;
      this.previousButton.disabled = firstVisible;
    }
  }
}

if (!customElements.get('complete-the-look-carousel')) {
  customElements.define('complete-the-look-carousel', CompleteTheLookCarousel);
}
